import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { DataService } from './data.service';
import { Observable, switchMap, timeout } from 'rxjs';
import { StorageReference } from "firebase/storage";
import { time } from 'console';

type GetBlobDownloadParams = ({ path: string } | { url: string } | { ref: StorageReference }) & { cnpj?: string };

export interface CheckListItemStorageConfig {
  /** Company id */
  company: string;
  /** OS id */
  OS: string;
  /** the checklist id */
  checklist: string;
  /** "image" | "audio" | "video"  */
  mediaType: string
  /** index of file into array, work as filename */
  fileIndex: number;
  /** the checklist-item id */
  checklistItem: string;
}

export const FIREBASE_STORAGE_PATHS = {
  companyLogo: ({ companyId }: { companyId: string }) => `${companyId}/logos/${companyId}companyLogo.jpg`,
  associationLogo: ({ companyId }: { companyId: string }) => `${companyId}/logos/${companyId}associationLogo.jpg`,
  userPhoto: ({ userId }: { userId: string }) => `users/avatar/${userId}.jpg`,
  clientPhoto: ({ companyId, clientId }: { companyId: string, clientId: string }) => `${companyId}/clients/${clientId}.jpg`,
  supplierPhoto: ({ companyId, supplierId }: { companyId: string, supplierId: string }) => `${companyId}/suppliers/${supplierId}.jpg`,
  /** Used to access the image of the applied breakdowns */
  breakdowns: ({ companyId, osId }: { companyId: string, osId: string }) => `${companyId}/breakdowns/${osId}`,
  /** Used to access images attached to a breakdown point */
  damagesAttachments: ({ companyId, osId, index }: { companyId: string, osId: string, index: number }) => `${companyId}/damages_attachments/${osId}/${index}`,
  /** Used to access the signature image applied to the common checklist */
  signature: ({ companyId, osId }: { companyId: string, osId: string }) => `${companyId}/signature/${osId}`,
  /** Used to access media (images, audio, video) applied to a common checklist item */
  checklist: (args: CheckListItemStorageConfig) => `${args.company}/checklistitem/${args.OS}/${args.checklist}/${args.checklistItem}/${args.mediaType}/${args.fileIndex}`,
  /** Used to access the default breakdown image of a checklist model */
  checklistByModel: ({ companyId, checklistModelId }: { companyId: string, checklistModelId: string }) => `${companyId}/checklistmodelitem/${checklistModelId}`,
  /** Used to access the breakdown image applied to the checklist model */
  checklistByModelBreakdown: ({ companyId, osId }: { companyId: string, osId: string }) => `${companyId}/checklistmodelitem/${osId}/breakdown`,
  /** Used to access the signature image applied to the checklist model */
  checklistByModelSignature: ({ companyId, osId }: { companyId: string, osId: string }) => `${companyId}/checklistmodelitem/${osId}/signature`,
  /** Used to access additional photos for the checklist model */
  checklistByModelPhoto: ({ companyId, osId, index }: { companyId: string, osId: string, index: number }) => `${companyId}/checklistmodelitem/${osId}/photos/${index}`,
}

@Injectable({
  providedIn: 'root'
})
export class FbImageDownloaderService {

  private _http = inject(HttpClient);
  private _dataService = inject(DataService);


  private _pathFromUrl(url: string) {
    const baseUrl = url.split("?")[0];
    const path = decodeURIComponent(baseUrl.split("/o/")[1]);
    return path;
  }

  /** Avoid CORS downloading Firebase resources */
  public getBlob(args: GetBlobDownloadParams): Observable<Blob | null> {
    const url = `${environment.mkgoURL}/api/v1/image-downloader/from-fb-storage`;
    const cnpj = args?.cnpj || this._dataService.company.cnpj;
    let path = "";
    if ('path' in args) {
      path = args.path;
    } else if ('url' in args) {
      path = this._pathFromUrl(args.url);
    } else if ('ref' in args) {
      path = args.ref.fullPath;
    }
    return this._dataService.httpOptions(cnpj).pipe(
      switchMap(options => this._http.post(url, { path }, { ...options, responseType: 'blob' })),
      timeout(10000)
    );
  }

}
